import { useEffect, useRef, useState } from "react";
import { segmentText, type GlossEntry } from "../../lib/glossed";

/**
 * Renders text with glossary words underlined; tapping one shows its meaning.
 * Splits on blank lines into paragraphs like the plain renderer.
 */
export function Glossed({ text, glossary }: { text: string; glossary: GlossEntry[] }) {
  const parts = text.split(/\n{2,}/).filter((p) => p.trim().length > 0);
  if (parts.length === 0) return null;
  return (
    <>
      {parts.map((p, i) => (
        <p key={i}>
          {segmentText(p, glossary).map((run, j) =>
            run.kind === "text" ? <span key={j}>{run.text}</span> : <GlossWord key={j} text={run.text} entry={run.entry} />,
          )}
        </p>
      ))}
    </>
  );
}

function GlossWord({ text, entry }: { text: string; entry: GlossEntry }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <span ref={ref} className="gloss">
      <button
        type="button"
        className={`gloss-word${open ? " open" : ""}`}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={`${text}: show meaning`}
      >
        {text}
      </button>
      {open && (
        <span className="gloss-pop" role="tooltip">
          <span className="gloss-pop-word">{entry.word}</span>
          <span className="gloss-pop-meaning">{entry.meaning}</span>
        </span>
      )}
    </span>
  );
}
